import { parseDataTransform, uploadFile } from '@acme/common'
import dayjs from 'dayjs'
import Dialog from 'global/guides/Dialog'
import { useAuth } from 'hooks'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
    afastamento,
    AfastamentoV2,
    afastamentoV2,
    ajustes,
    hasPermissionToAnyAfastamento,
} from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { LancamentosAnterioresAsync } from 'templates/ManutencaoDoPonto/Ausencias/lancamentos-anteriores'
import { useUpdateDigitacaoQueryData } from 'templates/ManutencaoDoPonto/Digitacao/pages/useDigitacaoData'
import { z } from 'zod'
import FormNovoAfastamento from '../forms/NovoAfastamento'
import AvisoAfastamento from './AvisoAfastamento'
import { funcionarioPodeSolicitarAjustes } from './DetalhesRegistros'

const baseUrl = import.meta.env.VITE_BASE_URL

export default function NovoAfastamentoModal() {
    const navigate = useNavigate()
    const { setQueryData } = useUpdateDigitacaoQueryData()
    const {
        decoded: { categoria },
    } = useAuth()
    const { mutateAsync, isLoading } = afastamentoV2.mutations.useCreate()
    const empresaId = useStore(state => state.companyId)
    const funcionarioId = useStore(state => state.employeeId)
    const [funcionarios, setIds] = React.useState<Array<number>>([
        funcionarioId,
    ])
    const [searchParams] = useSearchParams()
    const ocorrenciaId = searchParams.get('ocorrenciaId')
    const dia = searchParams.get('dia')
    const [values, setValues] = useState<Required<AfastamentoV2>>()
    const [isFetching, setIsFetching] = useState(false)

    const handleSubmit: SubmitHandler<Required<AfastamentoV2>> = async ({
        fileUrl,
        justificativa,
        ...data
    }) => {
        const filename =
            (await uploadFile(fileUrl, baseUrl).catch(e => {
                toast.error(e.data?.message || e.message)
                return null
            })) || null
        return mutateAsync(
            {
                ...parseDataTransform(data),
                justificativa: justificativa,
                fileUrl: filename,
                empresaId,
                funcionarios,
                origem: [3, 4, 6].includes(categoria) ? 1 : 2,
            },
            {
                onSuccess: async payload => {
                    const criados = payload.length
                    payload.forEach(p => {
                        if ('quantidadeHoras' in p)
                            return setQueryData('ABONO', p)
                        setQueryData('AFASTAMENTO', p)
                    })
                    await ajustes.aprovados.invalidateQueries()
                    if (
                        criados === 1 &&
                        !('quantidadeHoras' in payload[0]) &&
                        dayjs.utc(data.dataInicial).format('YYYY-MM-DD') ===
                            dayjs
                                .utc(payload[0].dataInicial)
                                .format('YYYY-MM-DD') &&
                        dayjs.utc(data.dataFinal).format('YYYY-MM-DD') ===
                            dayjs.utc(payload[0].dataFinal).format('YYYY-MM-DD')
                    ) {
                        toast.success('Afastamento cadastrado com sucesso')
                    } else if (!criados) {
                        toast.error(
                            'O afastamento não foi criado pois não pode ocupar o mesmo espaço que registros já existentes',
                        )
                    } else {
                        toast.warning(
                            'O afastamento foi criado, porém foi modificado para não ocupar o mesmo espaço de outros registros',
                        )
                    }
                    navigate(-1)
                },
                onError: err => {
                    toast.error(err.message)
                },
            },
        )
    }

    return (
        <Dialog.Root
            loading={isLoading || isFetching}
            onSubmit={async values => {
                setIsFetching(true)
                if (
                    await afastamento
                        .get('existe-registro', {
                            dataInicial: values.dataInicial,
                            dataFinal: values.dataFinal,
                            funcionarios,
                            empresaId,
                            ocorrenciaId: values.ocorrenciaId,
                        })
                        .catch(() => false)
                        .finally(() => setIsFetching(false))
                )
                    return setValues(values)
                handleSubmit(values)
            }}
            schema={z.object({
                ocorrenciaId: z.number().positive(),
                dataInicial: z.string().min(1),
                dataFinal: z.string().min(1),
                fileUrl: z.any().nullish(),
                justificativa: z.string().max(190).default(''),
            })}
            defaultValues={{
                ocorrenciaId: ocorrenciaId ? Number(ocorrenciaId) : undefined,
                dataInicial: dia,
                dataFinal: dia,
                dias: dia ? 1 : undefined,
            }}
            open={
                hasPermissionToAnyAfastamento('create') ||
                funcionarioPodeSolicitarAjustes()
            }
        >
            <Dialog.Title>Novo afastamento</Dialog.Title>
            <Dialog.Content>
                <FormNovoAfastamento
                    funcionarioId={funcionarioId}
                    setIds={setIds}
                />
                {funcionarios.length === 1 &&
                    funcionarios[0] === funcionarioId && (
                        <LancamentosAnterioresAsync tipoAusencia="Afastamento" />
                    )}
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton />
            </Dialog.Actions>
            <AvisoAfastamento
                open={!!values}
                onClose={() => setValues(undefined)}
                onSubmit={() => {
                    handleSubmit(values!)
                    setValues(undefined)
                }}
            />
        </Dialog.Root>
    )
}
